import {
  Button,
  Card,
  DatePicker,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Table,
  TableColumnType,
} from "antd";
import { Dayjs } from "dayjs";
import { useState } from "react";
import {
  ICreatePurchaseOrder,
  IPurchaseRequestDetail,
  IPurchaseRequestItem,
} from "../../models/Purchase";
import { useCreatePurchaseOrder } from "../../query/PurchaseQuery";
import { notify } from "../../utils/toast";

interface ICreatePurchaseOrderProps {
  open: boolean;
  onClose: () => void;
  purchaseRequest: IPurchaseRequestDetail;
}

interface IOrderItem extends IPurchaseRequestItem {
  quantityBuying: number;
  unitPriceExwVnd: number;
  totalExwVnd: number;
  totalImportTax: number;
}

interface IFormData {
  vendorName: string;
  orderDate: Dayjs | null;
  expectedDeliveryDate: Dayjs | null;
  shippingCharge: number;
  tax: number;
  note: string;
  exchangeRate: number;
}

const ModalCreatePurchaseOrder = ({
  open,
  onClose,
  purchaseRequest,
}: ICreatePurchaseOrderProps) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedItems, setSelectedItems] = useState<IOrderItem[]>([]);
  const [formData, setFormData] = useState<IFormData>({
    vendorName: "",
    orderDate: null,
    expectedDeliveryDate: null,
    shippingCharge: 0,
    tax: 0,
    note: "",
    exchangeRate: 26000,
  });

  const createPurchaseOrderMutation = useCreatePurchaseOrder();

  const handleSelectItems = (selectedKeys: React.Key[]) => {
    setSelectedRowKeys(selectedKeys);
    const newItems = purchaseRequest.items
      .filter((item) => selectedKeys.includes(item.id))
      .filter((item) => !selectedItems.find((si) => si.id === item.id))
      .map((item) => ({
        ...item,
        quantityBuying: item.quantity,
        unitPriceExwVnd: 0,
        totalExwVnd: 0,
        totalImportTax: 0,
      }));

    const keptItems = selectedItems.filter((item) =>
      selectedKeys.includes(item.id)
    );

    setSelectedItems([...keptItems, ...newItems]);
  };

  const updateOrderItem = (
    id: number,
    field: keyof IOrderItem,
    value: number
  ) => {
    setSelectedItems((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          const updated = { ...item, [field]: value };
          if (field === "quantityBuying" || field === "unitPriceExwVnd") {
            updated.totalExwVnd =
              updated.quantityBuying * updated.unitPriceExwVnd;
            updated.totalImportTax = (updated.totalExwVnd * formData.tax) / 100;
          }
          return updated;
        }
        return item;
      })
    );
  };

  const handleTaxPercentChange = (value: number | null) => {
    const newTaxPercent = value || 0;
    setSelectedItems((prev) =>
      prev.map((item) => ({
        ...item,
        totalImportTax: (item.totalExwVnd * newTaxPercent) / 100,
      }))
    );
  };

  const handleSubmit = async () => {
    try {
      if (
        !formData.vendorName ||
        !formData.orderDate ||
        !formData.expectedDeliveryDate
      ) {
        notify.error("Vui lòng điền đầy đủ thông tin bắt buộc");
        return;
      }

      if (selectedItems.length === 0) {
        notify.error("Vui lòng chọn ít nhất một vật tư");
        return;
      }
      const payload: ICreatePurchaseOrder = {
        purchaseRequestId: purchaseRequest.id,
        vendorName: formData.vendorName,
        orderDate: formData.orderDate.toDate(),
        expectedDeliveryDate: formData.expectedDeliveryDate.toDate(),
        shippingCharge: formData.shippingCharge || 0,
        importTaxPercent: formData.tax || 0,
        exchangeRate: formData.exchangeRate || 0,
        status: "DRAFT",
        note: formData.note || "",
        items: selectedItems.map((item) => ({
          partNumber: item.partNumber,
          description: item.description,
          unit: item.unit,
          quantityBuying: item.quantityBuying,
          unitPriceExwVnd: item.unitPriceExwVnd,
          totalExwVnd: item.totalExwVnd,
          purchaseRequestItemId: item.id,
        })),
      };

      await createPurchaseOrderMutation.mutateAsync(payload);
      notify.success("Tạo đơn đặt hàng thành công");
      handleClose();
    } catch (error) {
      notify.error("Lỗi khi tạo đơn đặt hàng");
    }
  };

  const handleClose = () => {
    setSelectedRowKeys([]);
    setSelectedItems([]);
    setFormData({
      vendorName: "",
      orderDate: null,
      expectedDeliveryDate: null,
      shippingCharge: 0,
      tax: 0,
      note: "",
      exchangeRate: 0,
    });
    onClose();
  };

  const availableColumns: TableColumnType<IPurchaseRequestItem>[] = [
    {
      title: "Part Number",
      dataIndex: "partNumber",
      key: "partNumber",
    },
    {
      title: "Mô tả",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Số lượng",
      dataIndex: "quantity",
      key: "quantity",
      render: (q: number, r: any) => `${q} ${r.unit || "pcs"}`,
    },
    {
      title: "Giá ước tính",
      dataIndex: "estimatedPrice",
      key: "estimatedPrice",
      render: (price: number) => `${Number(price).toLocaleString()} VNĐ`,
    },
  ];

  const orderColumns: TableColumnType<IOrderItem>[] = [
    {
      title: "Part Number",
      dataIndex: "partNumber",
      key: "partNumber",
      width: 150,
    },
    {
      title: "Mô tả",
      dataIndex: "description",
      key: "description",
      width: 200,
    },
    {
      title: "SL mua",
      key: "quantityBuying",
      width: 120,
      render: (_: any, record: IOrderItem) => (
        <InputNumber
          value={record.quantityBuying}
          onChange={(value) =>
            updateOrderItem(record.id, "quantityBuying", value || 0)
          }
          min={1}
          max={record.quantity}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Đơn giá EXW",
      key: "unitPriceExwVnd",
      width: 150,
      render: (_: any, record: IOrderItem) => (
        <InputNumber
          value={record.unitPriceExwVnd}
          onChange={(value) =>
            updateOrderItem(record.id, "unitPriceExwVnd", value || 0)
          }
          min={0}
          style={{ width: "100%" }}
          formatter={(value) =>
            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
          }
          parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ""))}
        />
      ),
    },
    {
      title: "Tổng EXW",
      dataIndex: "totalExwVnd",
      key: "totalExw",
      width: 150,
      render: (value: number) => `${Number(value).toLocaleString()} VNĐ`,
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Tạo đơn đặt hàng"
      width={1800}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Hủy
        </Button>,
        <Popconfirm
          title="Bạn có chắc chắn muốn tạo đơn đặt hàng?"
          onConfirm={handleSubmit}
          okText="Xác nhận"
          cancelText="Hủy"
        >
          <Button
            key="submit"
            type="primary"
            loading={createPurchaseOrderMutation.isPending}
            disabled={selectedItems.length === 0}
          >
            Tạo đơn đặt hàng
          </Button>
        </Popconfirm>,
      ]}
    >
      <div>
        <h4 className="mb-2">Chọn vật tư từ danh sách:</h4>
        <Table
          rowSelection={{
            selectedRowKeys,
            onChange: handleSelectItems,
          }}
          dataSource={purchaseRequest.items}
          columns={availableColumns}
          rowKey="id"
          pagination={false}
          size="small"
          scroll={{ y: 200 }}
        />

        {selectedItems.length > 0 && (
          <>
            <h4 className="mt-4 mb-2">Vật tư đã chọn - Điền thông tin:</h4>
            <Card title="Thông tin đặt hàng" size="small">
              <div className="mt-4 p-4 rounded border">
                <h4 className="mb-3 font-semibold text-blue-900">
                  Thông tin đơn hàng
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Tên nhà cung cấp
                    </label>
                    <Input
                      value={formData.vendorName}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          vendorName: e.target.value,
                        })
                      }
                      placeholder="Nhập tên nhà cung cấp"
                      size="large"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Ngày đặt hàng
                    </label>
                    <DatePicker
                      value={formData.orderDate}
                      onChange={(date) =>
                        setFormData({ ...formData, orderDate: date })
                      }
                      style={{ width: "100%" }}
                      placeholder="Chọn ngày đặt hàng"
                      size="large"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Ngày giao hàng
                    </label>
                    <DatePicker
                      value={formData.expectedDeliveryDate}
                      onChange={(date) =>
                        setFormData({
                          ...formData,
                          expectedDeliveryDate: date,
                        })
                      }
                      style={{ width: "100%" }}
                      placeholder="Chọn ngày giao hàng"
                      size="large"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Phí ship (VNĐ)
                    </label>
                    <InputNumber
                      value={formData.shippingCharge}
                      onChange={(value) =>
                        setFormData({
                          ...formData,
                          shippingCharge: value || 0,
                        })
                      }
                      style={{ width: "100%" }}
                      placeholder="0"
                      min={0}
                      formatter={(value) =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }
                      parser={(value) => Number(value!.replace(/,/g, ""))}
                      size="large"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Phí tax (%)
                    </label>
                    <InputNumber
                      value={formData.tax}
                      onChange={(value) => {
                        const newTax = value || 0;
                        setFormData({ ...formData, tax: newTax });
                        handleTaxPercentChange(newTax);
                      }}
                      style={{ width: "100%" }}
                      placeholder="0"
                      min={0}
                      max={100}
                      size="large"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Ghi chú
                    </label>
                    <Input
                      value={formData.note}
                      onChange={(e) =>
                        setFormData({ ...formData, note: e.target.value })
                      }
                      placeholder="Ghi chú thêm"
                      size="large"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Tỉ giá
                    </label>
                    <InputNumber
                      value={formData.exchangeRate}
                      onChange={(e) => {
                        const newExchangeRate = e || 0;
                        setFormData({
                          ...formData,
                          exchangeRate: newExchangeRate,
                        });
                      }}
                      placeholder="Tỉ giá"
                      size="large"
                    />
                  </div>
                </div>
              </div>
            </Card>
            <Table
              dataSource={selectedItems}
              columns={orderColumns}
              rowKey="id"
              pagination={false}
              size="small"
              scroll={{ x: 1000, y: 300 }}
            />
            <div className="mt-4 p-3 rounded">
              <div className="flex justify-between">
                <span className="font-semibold">Tổng EXW:</span>
                <span className="text-lg font-bold text-blue-600">
                  {selectedItems
                    .reduce((sum, item) => sum + item.totalExwVnd, 0)
                    .toLocaleString()}{" "}
                  VNĐ
                </span>
              </div>
              <div className="flex justify-between mt-2">
                <span className="font-semibold">Tổng thuế nhập khẩu:</span>
                <span className="text-lg font-bold text-orange-600">
                  {selectedItems
                    .reduce((sum, item) => sum + item.totalImportTax, 0)
                    .toLocaleString()}{" "}
                  VNĐ
                </span>
              </div>
              <div className="flex justify-between mt-2">
                <span className="font-semibold">Phí vận chuyển:</span>
                <span className="text-lg font-bold text-yellow-600">
                  {formData.shippingCharge.toLocaleString()} VNĐ
                </span>
              </div>
              <div className="flex justify-between mt-2 pt-2 border-t">
                <span className="font-semibold">Tổng cộng(VND):</span>
                <span className="text-xl font-bold text-green-600">
                  {(
                    selectedItems.reduce(
                      (sum, item) =>
                        sum + item.totalExwVnd + item.totalImportTax,
                      0
                    ) + formData.shippingCharge
                  ).toLocaleString()}{" "}
                  VNĐ
                </span>
              </div>
              <div className="flex justify-between mt-2 pt-2 border-t">
                <span className="font-semibold">Tổng cộng($):</span>
                <span className="text-xl font-bold text-green-600">
                  {(
                    (selectedItems.reduce(
                      (sum, item) =>
                        sum + item.totalExwVnd + item.totalImportTax,
                      0
                    ) +
                      formData.shippingCharge) /
                    formData.exchangeRate
                  ).toLocaleString()}
                  $
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ModalCreatePurchaseOrder;
